import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Car, Wrench, Clock, MapPin, Plus, Search, MessageCircle, Gift, Package, HelpCircle, History, CreditCard } from 'lucide-react';
import { CustomerService } from '../../services/customerService';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');

  const { data: serviceRequestsData, isLoading } = useQuery({
    queryKey: ['customerServiceRequests', user?.id],
    queryFn: () => CustomerService.getServiceRequests(user?.id),
    enabled: !!user?.id,
  });

  const serviceRequests = serviceRequestsData?.data || [];

  const onSiteServices = [
    { id: 'roadside', name: 'Roadside Assistance', icon: '⚠️', color: '#ef4444' },
    { id: 'jump-start', name: 'Jump Start', icon: '🔋', color: '#f59e0b' },
    { id: 'towing', name: 'Towing', icon: '🚛', color: '#8b5cf6' },
    { id: 'fuel', name: 'Fuel Delivery', icon: '⛽', color: '#10b981' },
    { id: 'tyre', name: 'Tyre Change', icon: '🛞', color: '#3b82f6' },
    { id: 'lockout', name: 'Lockout Service', icon: '🔑', color: '#ec4899' },
  ];

  const garageServices = [
    { id: 'periodic', name: 'Periodic Service', icon: '📅', color: '#6366f1' },
    { id: 'ac', name: 'AC Service', icon: '❄️', color: '#06b6d4' },
    { id: 'battery', name: 'Battery', icon: '🔋', color: '#10b981' },
    { id: 'tyres', name: 'Tyres', icon: '🛞', color: '#f59e0b' },
    { id: 'dent-paint', name: 'Dent & Paint', icon: '🎨', color: '#ec4899' },
    { id: 'detailing', name: 'Detailing', icon: '✨', color: '#8b5cf6' },
    { id: 'washing', name: 'Car Wash', icon: '💧', color: '#3b82f6' },
    { id: 'glass', name: 'Windshield', icon: '🪟', color: '#14b8a6' },
  ];

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
    if (s === 'pending') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Welcome Back!</h1>
            <p className="text-white/80 flex items-center">
              <MapPin size={16} className="mr-2" />
              Delhi, India
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/customer/vehicles')}
              className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center space-x-2"
            >
              <Plus size={18} />
              <span>Add Vehicle</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <button
          onClick={() => navigate('/customer/vehicles')}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition flex items-center space-x-3"
        >
          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
            <Car size={24} className="text-primary-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">My Vehicles</p>
            <p className="text-sm text-gray-500">Manage vehicles</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/customer/chat')}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition flex items-center space-x-3"
        >
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <MessageCircle size={24} className="text-green-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">Chat Support</p>
            <p className="text-sm text-gray-500">Get help</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/customer/track')}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition flex items-center space-x-3"
        >
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Package size={24} className="text-blue-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">Track Order</p>
            <p className="text-sm text-gray-500">Track shipments</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/customer/offers')}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition flex items-center space-x-3"
        >
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <Gift size={24} className="text-purple-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">Offers</p>
            <p className="text-sm text-gray-500">View deals</p>
          </div>
        </button>
      </div>

      {/* Additional Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => navigate('/customer/service-history')}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition flex items-center space-x-3"
        >
          <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
            <History size={24} className="text-indigo-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">Service History</p>
            <p className="text-sm text-gray-500">Past services</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/customer/payment-history')}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition flex items-center space-x-3"
        >
          <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
            <CreditCard size={24} className="text-teal-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">Payment History</p>
            <p className="text-sm text-gray-500">View receipts</p>
          </div>
        </button>

        <button
          onClick={() => navigate('/customer/support')}
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition flex items-center space-x-3"
        >
          <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
            <HelpCircle size={24} className="text-yellow-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900">Help & Support</p>
            <p className="text-sm text-gray-500">Get assistance</p>
          </div>
        </button>
      </div>

      {/* On-Site Services */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">On-Site Services</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {onSiteServices.map((service) => (
            <button
              key={service.id}
              onClick={() => navigate('/customer/services')}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition text-center"
            >
              <div className="text-3xl mb-2">{service.icon}</div>
              <p className="text-sm font-medium text-gray-900">{service.name}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Garage Services */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">Garage Services</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
          {garageServices.map((service) => (
            <button
              key={service.id}
              onClick={() => navigate('/customer/services')}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition text-center"
            >
              <div className="text-3xl mb-2">{service.icon}</div>
              <p className="text-sm font-medium text-gray-900">{service.name}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Services */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Recent Services</h2>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading services...</p>
          </div>
        ) : serviceRequests.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Wrench size={48} className="mx-auto mb-4 text-gray-400" />
            <p>No recent services</p>
            <button
              onClick={() => navigate('/customer/services')}
              className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              Book a Service
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {serviceRequests.slice(0, 5).map((request) => (
              <div key={request.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{request.service_type || 'Service Request'}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                      {request.status || 'Pending'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{request.description || 'No description'}</p>
                  {request.preferred_date && (
                    <p className="text-xs text-gray-500 mt-1">
                      Preferred: {new Date(request.preferred_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => navigate('/customer/services')}
                  className="ml-4 px-3 py-2 text-sm text-primary-600 hover:text-primary-700"
                >
                  View Details
                </button>
              </div>
            ))}
            {serviceRequests.length > 5 && (
              <button
                onClick={() => navigate('/customer/services')}
                className="w-full mt-4 px-4 py-2 text-primary-600 hover:text-primary-700 font-medium"
              >
                View All Services ({serviceRequests.length})
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;

